import { Field, Form, Formik } from 'formik'
import React, { useMemo } from 'react'

import { Dashboard, DashboardBackButton, PageLayout } from '@components'
import {
  Button,
  ButtonClean,
  Fieldset,
  HeadContent,
  Icon,
  Input,
  Subsection,
  Tag,
} from '@fourthwall/components'
import { TickIcon, TrashIcon } from '@fourthwall/icons'
import { routing } from '@utils/routing'

import logoSrc from '../../assets/signets/meta-capi.svg'
import { ConnectBar } from '../../components/ConnectBar/ConnectBar'
import type { SettingsFormValues, TestEventFormValues } from './AppsMetaConversions.types'
import { ConnectValidationSchema } from './consts'
import { useMetaConversionPixel } from './hooks/useMetaConversionPixel'

export const AppsMetaConversions = () => {
  const {
    pixelId,
    testEventCode,
    updatePixel,
    updateTestEventCode,
    deletePixel,
    deleteTestEventCode,
    loadingTestEventCode,
  } = useMetaConversionPixel()
  const isConnected = useMemo(() => !!pixelId, [pixelId])

  const renderStatusTag = () => {
    const props = isConnected
      ? {
          label: 'Connected',
          appearance: 'success' as const,
        }
      : {
          label: 'Not Connected',
          appearance: 'alert' as const,
        }

    return (
      <div style={{ marginBottom: 20 }}>
        <Tag {...props} size="large" display="inline-flex" />
      </div>
    )
  }

  return (
    <>
      <HeadContent title="Meta Ads" />
      <PageLayout size="tiny">
        <Dashboard.Section
          name="Meta Ads"
          topContent={<DashboardBackButton label="Apps" to={routing.apps.self} />}
        >
          <Subsection>
            {renderStatusTag()}
            <div>
              Send conversion events to Meta's Ads Manager if you run paid ad campaigns for your
              shop.{' '}
              <ButtonClean
                href={routing.external.helpCenter.metaCAPI}
                label="Learn more"
                target="_blank"
              />
            </div>
          </Subsection>
          {!isConnected && (
            <Subsection title="Connection">
              <Formik<SettingsFormValues>
                initialValues={{}}
                validationSchema={ConnectValidationSchema}
                onSubmit={({ pixelId, accessToken }) =>
                  updatePixel({ pixelId: pixelId!, accessToken: accessToken! })
                }
              >
                {({ isSubmitting }) => (
                  <Form>
                    <div style={{ marginBottom: 16 }}>
                      Use your Conversions API Pixel ID and Access Token from your Meta Events
                      Manager dashboard.{' '}
                      <ButtonClean
                        href={routing.external.helpCenter.metaCAPI}
                        label="Where can I find this?"
                        target="_blank"
                      />
                    </div>
                    <Fieldset>
                      <Fieldset.Row>
                        <Fieldset.Item>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <Field component={Input} name="pixelId" label="Pixel ID" />
                            <Field
                              component={Input}
                              name="accessToken"
                              label="Access Token"
                              type="password"
                            />
                          </div>
                        </Fieldset.Item>
                      </Fieldset.Row>
                    </Fieldset>
                    <div style={{ marginTop: 16 }}>
                      <Button
                        type="submit"
                        label="Add Conversions API Pixel"
                        appearance="primary"
                        loading={isSubmitting}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </Subsection>
          )}
          {isConnected && (
            <Subsection title="Connection">
              <div style={{ marginBottom: 16 }}>
                <p style={{ marginBottom: 16 }}>
                  <ConnectBar
                    logoSrc={logoSrc}
                    description={`Pixel ${pixelId}`}
                    tag={null}
                    onDisconnect={() => deletePixel()}
                  />
                </p>
              </div>
            </Subsection>
          )}
          {isConnected && (
            <Subsection title="Test Events">
              <div style={{ marginBottom: 16 }}>
                Use a test event code to verify your pixel implementation and test Conversions API
                tracking.{' '}
                <ButtonClean
                  href="https://www.facebook.com/business/help/1624255387706033?id=818859032317965"
                  label="Learn more"
                  target="_blank"
                />
              </div>
              <Formik<TestEventFormValues>
                initialValues={{ testEventCode: testEventCode || '' }}
                enableReinitialize
                onSubmit={async ({ testEventCode }) => {
                  if (testEventCode) {
                    await updateTestEventCode(testEventCode)
                  }
                }}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <Fieldset>
                      <Fieldset.Row>
                        <Fieldset.Item>
                          <div
                            style={{
                              display: 'flex',
                              alignContent: 'center',
                              alignItems: 'center',
                              gap: 12,
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <Field
                                component={Input}
                                name="testEventCode"
                                label="Test Event Code"
                                placeholder="TEST12345"
                                disabled={!!testEventCode}
                              />
                            </div>
                            {!testEventCode && (
                              <Button
                                type="submit"
                                icon={<Icon component={TickIcon} width={16} height={16} />}
                                appearance="primary"
                                loading={isSubmitting || loadingTestEventCode}
                              />
                            )}
                            {testEventCode && (
                              <Button
                                type="button"
                                icon={<Icon component={TrashIcon} width={16} height={16} />}
                                appearance="destructive-inverted"
                                loading={loadingTestEventCode}
                                onClick={() => deleteTestEventCode()}
                              />
                            )}
                          </div>
                        </Fieldset.Item>
                      </Fieldset.Row>
                    </Fieldset>
                  </Form>
                )}
              </Formik>
            </Subsection>
          )}
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
